import { useCallback, useState, useMemo, useEffect } from 'react';
import { kebabCase } from 'lodash';
import type {
  Brand,
  Category,
  Collection,
  DataLoaders,
  Dispensary,
  Product as RemoteProduct,
  Special,
} from '@dutchiesdk/ecommerce-extensions-sdk';
import { useRouter } from 'next/compat/router';
import { useApolloClient } from '@apollo/react-hooks';

import {
  GqlBrand,
  GqlCollection,
  GqlIndividualFilteredProductQuery,
  GqlSpecials,
  useGetCollectionsQuery,
  GetRemoteIntegrationValueDocument,
  useIndividualFilteredProductQuery,
} from 'types/graphql';
import { Product } from 'types/graphql-overrides';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useDispensaryCategories from 'src/dispensary/hooks/use-dispensary-categories';
import useMenuFilterOptions from 'hooks/use-menu-filter-options';
import useDispensarySpecials from 'src/dispensary/hooks/use-dispensary-specials';
import useDispensaryProducts from 'src/dispensary/hooks/use-dispensary-products';
import type { DispensaryMenuCategory } from 'src/dispensary/hooks/use-dispensary-categories';
import { useDispensaryChains } from 'src/dispensary/hooks/use-dispensary-chain';
import { WhenNotVoid } from 'shared/utils/type-utils';
import { buildCategoryRoute } from 'shared/helpers/route-helpers';
import { getVariables } from 'src/hooks/use-product';
import useUI from 'src/hooks/use-ui';
import { mapDispensaryToRemote } from './bridge-map-dispensary';

type ResolvablePromise<T> = Promise<T> & {
  resolve?: (value: T) => void;
};

function useCreateLazyDataLoader<T>(
  useData: (skip: boolean) => { data: T; loading: boolean },
  dependencies: any[] = []
): { data: T; dataLoader: () => Promise<T> } {
  const [shouldFetch, setShouldFetch] = useState<boolean>(false);
  // create once
  const resolvablePromise = useMemo<ResolvablePromise<T>>(() => {
    let outerResolve: ((value: T) => void) | null = null;
    const promise: ResolvablePromise<T> = new Promise<T>((resolve) => {
      outerResolve = resolve;
    });
    // we know this is assigned because the callback in promises constructor is called syncronously
    promise.resolve = (outerResolve as unknown) as (value: T) => void;

    return promise;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies]);

  const { data, loading } = useData(!shouldFetch);

  useEffect(() => {
    if (resolvablePromise.resolve && shouldFetch && !loading) {
      const { resolve } = resolvablePromise;
      delete resolvablePromise.resolve;
      resolve(data);
    }
  }, [data, loading, shouldFetch, resolvablePromise]);

  const dataLoader = useCallback(async () => {
    setShouldFetch(true);

    return resolvablePromise;
  }, [resolvablePromise]);

  return { data, dataLoader };
}

export const useBridgeDataLoaders = (): DataLoaders => {
  const apolloClient = useApolloClient();
  const { dispensary } = useDispensary();
  const UI = useUI();
  const { categories } = useDispensaryCategories();
  const { brands } = useMenuFilterOptions({ skipCurrentFilters: true });
  const router = useRouter();
  const { query } = router ?? {};
  const { isDemo, isKiosk, isEmbedded, isStoreFront, isMobileEcommApp } = UI;

  // Expensive Data Loaders - Use lazy loading to only load when requested.

  // SPECIALS
  const { dataLoader: getAllSpecials } = useCreateLazyDataLoader((skip) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { specials, loading } = useDispensarySpecials({
      skip,
    });

    return { data: specials, loading: skip || loading };
  });

  // PRODUCTS
  const { dataLoader: getAllProducts } = useCreateLazyDataLoader((skip) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { products, loading } = useDispensaryProducts({
      skip,
    });

    return { data: products, loading: skip || loading };
  });

  const { dataLoader: getProduct } = useCreateLazyDataLoader(
    (skip) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { data: individualFilteredProductData, loading } = useIndividualFilteredProductQuery({
        variables: getVariables(query?.productId, dispensary, UI.isKiosk),
        skip: skip || !query?.productId,
      });

      return { data: individualFilteredProductData?.filteredProducts?.products[0], loading: skip || loading };
    },
    [query?.productId]
  );

  // LOCATIONS
  const { dataLoader: getAllLocations } = useCreateLazyDataLoader((skip) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { dispensaryChainLocations, loading } = useDispensaryChains({
      activeOnly: true,
      includeStealthMode: true,
      skip,
    });

    return { data: dispensaryChainLocations ?? [], loading: skip || loading };
  });

  // COLLECTIONS
  const { dataLoader: getAllCollections } = useCreateLazyDataLoader((skip) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data: collectionsData, loading } = useGetCollectionsQuery({
      skip: skip || !dispensary?.id,
      variables: {
        dispensaryId: dispensary?.id ?? '',
        customCategory: false,
      },
    });

    return { data: collectionsData?.getCollections ?? [], loading: skip || loading };
  });

  // INTEGRATION VALUE
  const getIntegrationValue = useCallback(
    async (key = '') => {
      const result = await apolloClient.query({
        query: GetRemoteIntegrationValueDocument,
        variables: {
          dispensaryId: dispensary?.id ?? '',
          key,
        },
      });
      return result.data;
    },
    [apolloClient, dispensary?.id]
  );

  const mapCategoryToRemote = createMapCategoryToRemote({
    isDemo,
    isKiosk,
    isEmbedded,
    isStoreFront,
    isMobileEcommApp,
    dispensary,
  });

  return {
    brands: async () => {
      const result = await Promise.resolve(brands);
      return result.map(mapBrandToRemote);
    },
    categories: async () => {
      const result = await Promise.resolve(categories);
      return result.map(mapCategoryToRemote);
    },
    collections: async () => {
      const result = await getAllCollections();
      return result.map(mapCollectionToRemote);
    },
    integrationValue: async (key: string) => {
      const result = await getIntegrationValue(key);
      return result?.getRemoteIntegrationValue ?? undefined;
    },
    locations: async () => {
      const result = await getAllLocations();
      return result.map(mapDispensaryToRemote).filter((d): d is Dispensary => d !== undefined);
    },
    product: async () => {
      const result = await getProduct();
      return result ? mapProductToRemote(result) : null;
    },
    products: async () => {
      const result = await getAllProducts();
      return result.map(mapProductToRemote);
    },
    specials: async () => {
      const result = await getAllSpecials();
      return result.map(mapSpecialToRemote);
    },
  };
};

const mapBrandToRemote = (brand: GqlBrand): Brand => ({
  cname: kebabCase(brand.name),
  id: brand.id,
  image: brand.imageUrl ?? '',
  name: brand.name,
});

function createMapCategoryToRemote({ isDemo, isKiosk, isEmbedded, isStoreFront, isMobileEcommApp, dispensary }) {
  return (category: DispensaryMenuCategory): Category & { href: string; route: string } => {
    const [href, route] = buildCategoryRoute({
      isDemo,
      isKiosk,
      isEmbedded,
      isStoreFront,
      isMobileEcommApp,
      dispensary,
      category: category.key,
    });

    return {
      cname: category.slug ?? category.key,
      id: category.key,
      name: category.label,
      href,
      route,
    };
  };
}

const mapCollectionToRemote = (collection: GqlCollection): Collection => ({
  cname: collection.slug,
  id: collection.id,
  name: collection.title,
});

const mapProductToRemote = (
  product: Product | WhenNotVoid<WhenNotVoid<GqlIndividualFilteredProductQuery['filteredProducts']>['products'][number]>
): RemoteProduct => {
  const price = (product as Product).prices ?? product.Prices;
  return {
    cname: product.cName ?? product.id ?? '',
    description: product.description ?? '',
    id: product.id ?? '',
    image: product.Image ?? '',
    name: product.Name ?? '',
    price: price?.[0] ?? 0,
  };
};

const mapSpecialToRemote = (special: GqlSpecials): Special => ({
  cname: special._id ?? '',
  description: special.menuDisplayDescription ?? '',
  id: special._id ?? '',
  image: special.menuDisplayName ?? '',
  name: special.name ?? '',
});
